/**
 * configurator-host.js — de parent-zijde van het contract (masterplan §3.1,
 * §4.3, §4.4). Draait in het thema én ongewijzigd in de lokale harness.
 *
 * Verwacht een globale configuratie, gezet door de snippet of de harness:
 *
 *   window.MemuroConfigurator = {
 *     slug: 'dummy',
 *     configOrigin: 'https://config.memuro.nl',   // waar de apps staan
 *     appUrl: '…',                                // volledige iframe-URL
 *     shop: 'memuro.nl',
 *     productHandle: '…', productId: '…',
 *     locale: 'nl', currency: 'EUR',
 *     variants: [{ id, title, price, available, widthMm?, heightMm?, squareMm? }],
 *   };
 *
 * Drie verdedigingen die geen franje zijn:
 *  1. origin-allowlist op elk bericht — nooit een wildcard (§3.1)
 *  2. form-guard: herschrijft de hidden inputs als het thema de DOM vervangt (§4.3)
 *  3. variant-invalidatie: wisselt de klant van variant ná cfg:done, dan gaan
 *     de inputs weg en de koopknop op slot (§4.4)
 */
(function () {
  'use strict';
  const PROTOCOL = 1;
  const CFG = window.MemuroConfigurator;
  if (!CFG || !CFG.slug) return;

  const state = {
    config: null,       // { configId, previewUrl, summary, app, appVersion, variantId }
    modal: null,
    iframe: null,
  };

  // ---- formulier vinden en vasthouden (§4.3) --------------------------------

  function productForm() {
    // Dawn rendert TWEE formulieren met action /cart/add: de echte koopknop-form
    // (in <product-form>, mét een [name="add"]-knop) en de Shop Pay
    // installment-banner in het prijsblok (class "installment", geen knop).
    // Blind de eerste pakken koppelt de properties aan de banner — dan komen ze
    // nooit op de order. Kies dus de form die de koopknop bevat.
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i += 1) {
      if (forms[i].querySelector('[name="add"]')) return forms[i];
    }
    for (var j = 0; j < forms.length; j += 1) {
      if (!forms[j].classList.contains('installment')) return forms[j];
    }
    return forms[0] || null;
  }

  /**
   * NOOIT form.id gebruiken op een Shopify-productform: elk productformulier
   * bevat <input name="id"> (de variantkeuze), en formuliervelden
   * overschaduwen properties op het formulierelement. form.id geeft dan dat
   * input-ELEMENT terug in plaats van de id-string — en alles wat daarop
   * bouwt schrijft form="[object HTMLInputElement]". Altijd getAttribute.
   */
  function formElementId(form) {
    let id = form.getAttribute('id');
    if (!id) { id = 'product-form-cfg'; form.setAttribute('id', id); }
    return id;
  }

  function formId() {
    const form = productForm();
    return form ? formElementId(form) : null;
  }

  function selectedVariantId() {
    const form = productForm();
    const input = form && form.querySelector('[name="id"]');
    return input ? String(input.value) : String(CFG.variants[0] && CFG.variants[0].id || '');
  }

  function setAtcEnabled(enabled) {
    const form = productForm();
    if (!form) return;
    form.querySelectorAll('button[type="submit"], [name="add"]').forEach(function (btn) {
      btn.disabled = !enabled;
      btn.setAttribute('aria-disabled', String(!enabled));
    });
  }

  // ---- hidden inputs (§3.2) -------------------------------------------------

  const HOLDER_ID = 'memuro-config-inputs';

  function writeHiddenInputs(config) {
    removeHiddenInputs();
    const form = productForm();
    if (!form) return;
    const id = formElementId(form);
    const holder = document.createElement('div');
    holder.id = HOLDER_ID;
    holder.hidden = true;

    const props = [
      ['_config_id', config.configId],
      ['_config_app', config.app + '@' + config.appVersion],
      ['Voorbeeld', config.previewUrl],
    ];
    for (const item of validSummary(config.summary)) props.push([item.label, item.value]);

    for (const pair of props) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[' + pair[0] + ']';
      input.value = pair[1];
      input.setAttribute('form', id);
      holder.append(input);
    }
    // ÍN het formulier, niet ernaast: dan telt hij mee in elke serialisatie,
    // ongeacht hoe het thema de FormData opbouwt. Vervangt Dawn het formulier
    // bij een variantwissel, dan zet de MutationObserver-guard hem terug.
    form.append(holder);
  }

  /** Diagnose: hoeveel properties ziet de browser echt in de FormData? */
  function countLinkedProperties() {
    const form = productForm();
    if (!form) return { formId: '(geen form)', count: 0 };
    let count = 0;
    new FormData(form).forEach(function (value, key) {
      if (key.indexOf('properties[') === 0) count += 1;
    });
    return { formId: '#' + formElementId(form), count: count };
  }

  function removeHiddenInputs() {
    const holder = document.getElementById(HOLDER_ID);
    if (holder) holder.remove();
  }

  /** §3.1: max 6, geen leidende underscore, label ≤ 40, waarde ≤ 120.
   *  Wat niet valideert wordt weggegooid, niet gecorrigeerd. */
  function validSummary(summary) {
    if (!Array.isArray(summary)) return [];
    const out = [];
    for (const item of summary) {
      if (out.length >= 6) break;
      if (!item || typeof item.label !== 'string' || typeof item.value !== 'string') continue;
      const label = item.label.trim();
      const value = item.value.trim();
      if (!label || label.charAt(0) === '_' || label.length > 40) continue;
      if (!value || value.length > 120) continue;
      out.push({ label: label, value: value });
    }
    return out;
  }

  // Form-guard: het thema kan het formulier of de body-inhoud vervangen.
  const guard = new MutationObserver(function () {
    if (state.config && !document.querySelector('input[name="properties[_config_id]"]')) {
      writeHiddenInputs(state.config);
    }
  });
  guard.observe(document.documentElement, { childList: true, subtree: true });

  // ---- variantwissel (§4.4) -------------------------------------------------

  let lastVariantId = null;

  function onVariantMaybeChanged() {
    const current = selectedVariantId();
    if (current === lastVariantId) return;
    lastVariantId = current;

    // Naar de iframe, als die open staat.
    postToApp('host:variant-changed', { variantId: current });

    // Slot 1 van §4.4: configuratie hoort bij één formaat.
    if (state.config && state.config.variantId !== current) {
      state.config = null;
      removeHiddenInputs();
      setAtcEnabled(false);
      setBadge('Je koos een ander formaat. Pas je ontwerp aan.', true);
    }
  }

  document.addEventListener('change', function (e) {
    const form = productForm();
    if (form && (form.contains(e.target) || e.target.getAttribute('form') === formElementId(form))) {
      // Dawn werkt input[name=id] async bij; wacht één tik.
      setTimeout(onVariantMaybeChanged, 0);
    }
  });
  // Vangnet voor thema's die de waarde programmatisch zetten.
  setInterval(onVariantMaybeChanged, 800);

  // ---- badge / status onder de knop ----------------------------------------

  function setBadge(text, isWarning) {
    let el = document.getElementById('memuro-config-badge');
    if (!el) {
      el = document.createElement('p');
      el.id = 'memuro-config-badge';
      el.style.cssText = 'font-size:15px;line-height:1.4;margin:.6rem 0 0;';
      const button = document.getElementById('memuro-config-open');
      if (button && button.parentNode) button.parentNode.insertBefore(el, button.nextSibling);
    }
    el.textContent = text;
    el.style.color = isWarning ? '#a11' : 'inherit';
  }

  // ---- modal ----------------------------------------------------------------

  function openModal() {
    if (state.modal) return;
    const modal = document.createElement('div');
    modal.style.cssText =
      'position:fixed;inset:0;z-index:9999;background:rgba(10,10,10,.55);' +
      'display:flex;align-items:stretch;justify-content:center;padding:0;';
    const iframe = document.createElement('iframe');
    iframe.src = CFG.appUrl;
    iframe.title = 'Configurator';
    iframe.setAttribute('allow', 'clipboard-write');
    iframe.setAttribute('referrerpolicy', 'strict-origin');
    iframe.style.cssText = 'border:0;width:100%;height:100%;background:#fffaf0;';
    modal.append(iframe);
    document.body.append(modal);
    document.body.style.overflow = 'hidden';
    state.modal = modal;
    state.iframe = iframe;

    modal.addEventListener('click', function (e) {
      if (e.target === modal) postToApp('host:close-request', {});
    });
    document.addEventListener('keydown', onEscape);
  }

  function onEscape(e) {
    if (e.key === 'Escape' && state.modal) postToApp('host:close-request', {});
  }

  function closeModal() {
    if (!state.modal) return;
    state.modal.remove();
    state.modal = null;
    state.iframe = null;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onEscape);
  }

  function postToApp(type, payload) {
    if (!state.iframe || !state.iframe.contentWindow) return;
    state.iframe.contentWindow.postMessage(
      { v: PROTOCOL, type: type, payload: payload },
      CFG.configOrigin,                            // expliciete origin, nooit '*'
    );
  }

  // ---- berichten van de app (§3.1) -----------------------------------------

  window.addEventListener('message', function (event) {
    if (event.origin !== CFG.configOrigin) return;           // harde allowlist
    if (!state.iframe || event.source !== state.iframe.contentWindow) return;
    const msg = event.data;
    if (!msg || msg.v !== PROTOCOL || typeof msg.type !== 'string') return;

    if (msg.type === 'cfg:ready') {
      postToApp('host:context', {
        shop: CFG.shop,
        productHandle: CFG.productHandle,
        productId: String(CFG.productId),
        locale: CFG.locale, currency: CFG.currency,
        variants: CFG.variants.map(function (v) {
          return {
            id: String(v.id), title: v.title, price: v.price,
            available: v.available, widthMm: v.widthMm, heightMm: v.heightMm,
            squareMm: v.squareMm,          // vierkante vorm (custom.square_mm), besluit 18-08
          };
        }),
        selectedVariantId: selectedVariantId(),
      });
    } else if (msg.type === 'cfg:done') {
      const p = msg.payload || {};
      if (!p.configId || !/^cfg_[a-z0-9]+$/.test(String(p.configId))) return;
      state.config = {
        configId: String(p.configId),
        previewUrl: String(p.previewUrl || ''),
        summary: p.summary,
        app: String(p.app || CFG.slug),
        appVersion: String(p.appVersion || '0.0.0'),
        variantId: String(p.variantId),
      };
      // §4.4 slot 1, ook bij afronden: klopt het formaat niet met de huidige
      // variantkeuze, dan niet aannemen.
      if (state.config.variantId !== selectedVariantId()) {
        state.config = null;
        closeModal();
        setBadge('Het formaat in je ontwerp komt niet overeen met de gekozen variant.', true);
        return;
      }
      writeHiddenInputs(state.config);
      setAtcEnabled(true);
      closeModal();
      // 05-09: meteen aan de winkelwagen toevoegen via het echte productformulier
      // van het thema (requestSubmit vuurt Dawn's eigen submit-afhandeling af:
      // fetch naar /cart/add en de winkelwagenlade of -melding gaat open). Het
      // gekozen formaat, de lijst-optie en het aantal gaan zo gewoon mee. Lukt
      // dat niet (oud thema, geen formulier), dan blijft het oude gedrag: de
      // klant klikt zelf op de koopknop.
      var form = productForm();
      if (form && typeof form.requestSubmit === 'function') {
        var koop = form.querySelector('[name="add"]');
        setBadge('Je ontwerp is toegevoegd aan de winkelwagen.', false);
        form.requestSubmit(koop || undefined);
      } else {
        setBadge('Je ontwerp is opgeslagen en zit bij dit product. Klik nu op "Aan winkelwagen toevoegen".', false);
      }
    } else if (msg.type === 'cfg:cancel') {
      closeModal();
    } else if (msg.type === 'cfg:error') {
      closeModal();
      setBadge('Er ging iets mis in de configurator: ' + (msg.payload && msg.payload.message || 'onbekende fout'), true);
    }
  });

  // ---- start ----------------------------------------------------------------

  /**
   * Als de knop uit de losse sectie `memuro-configurator` komt (aangehangen
   * aan een producttemplate, zodat main-product.liquid van het live thema
   * onaangeraakt blijft), staat hij onder de productinformatie. Zet hem dan
   * op de plek waar hij hoort: vlak boven de koopknoppen in het echte
   * koopformulier. Lukt dat niet (ander thema, andere klassen), dan blijft hij
   * gewoon in zijn sectie staan en werkt hij ook.
   */
  function relocateButton(button) {
    if (!button || !button.closest('[data-memuro-standalone]')) return;
    const form = productForm();
    if (!form) return;
    const buttons = form.querySelector('.product-form__buttons');
    const anchor = buttons || form.querySelector('[name="add"]');
    if (!anchor || !anchor.parentNode) return;
    anchor.parentNode.insertBefore(button, anchor);
  }

  function init() {
    lastVariantId = selectedVariantId();
    setAtcEnabled(false);                    // §4.6: op slot tot cfg:done
    const button = document.getElementById('memuro-config-open');
    relocateButton(button);
    if (button) button.addEventListener('click', openModal);

    // M1.2-testhaak: ?cfgtest=1 schrijft vaste waarden in de hidden inputs en
    // opent de koopknop zonder configurator — puur om de property-doorstroom
    // naar de order te bewijzen (roadmap M1.2). Zonder de parameter is dit een
    // no-op; na M1.2 mag dit blok weg.
    if (new URLSearchParams(window.location.search).get('cfgtest') === '1') {
      // Uniek id per paginalading: twee keer toevoegen met een herlaad ertussen
      // moet twee áparte orderregels geven (het klassieke struikelblok, §4.6).
      var testId = 'cfg_m12' + Math.random().toString(36).slice(2, 8);
      state.config = {
        configId: testId,
        previewUrl: 'https://cdn.memuro.nl/p/m12-testvoorbeeld.png',
        summary: [{ label: 'Kleur', value: 'Caramel (M1.2-test)' }],
        app: 'dummy', appVersion: '0.1.0',
        variantId: selectedVariantId(),
      };
      writeHiddenInputs(state.config);
      setAtcEnabled(true);
      var diag = countLinkedProperties();
      setBadge(
        'M1.2 v4 · ' + testId + ' · ' + diag.count + ' properties gekoppeld aan ' + diag.formId +
        (diag.count === 0 ? ' — FOUT: niets gekoppeld' : ' — OK, voeg toe aan winkelwagen'),
        diag.count === 0,
      );
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
